import React from 'react';
import { AlertCircle, CheckCircle, Clock, Flame } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const PriorityMatrix = ({ tasks, onTaskClick }) => {
  const categorizeTask = (task) => {
    const urgency = task?.urgency || 'medium';
    const importance = task?.importance || 'medium';

    if ((urgency === 'critical' || urgency === 'high') && (importance === 'critical' || importance === 'high')) {
      return 'urgent-important';
    } else if ((urgency === 'low' || urgency === 'medium') && (importance === 'critical' || importance === 'high')) {
      return 'not-urgent-important';
    } else if ((urgency === 'critical' || urgency === 'high') && (importance === 'low' || importance === 'medium')) {
      return 'urgent-not-important';
    } else {
      return 'not-urgent-not-important';
    }
  };

  const quadrants = {
    'urgent-important': {
      title: 'Do First',
      subtitle: 'Urgent & Important',
      color: 'red',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      icon: Flame,
      tasks: tasks?.filter(t => categorizeTask(t) === 'urgent-important')
    },
    'not-urgent-important': {
      title: 'Schedule',
      subtitle: 'Not Urgent & Important',
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      icon: Clock,
      tasks: tasks?.filter(t => categorizeTask(t) === 'not-urgent-important')
    },
    'urgent-not-important': {
      title: 'Delegate',
      subtitle: 'Urgent & Not Important',
      color: 'yellow',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      icon: AlertCircle,
      tasks: tasks?.filter(t => categorizeTask(t) === 'urgent-not-important')
    },
    'not-urgent-not-important': {
      title: 'Eliminate',
      subtitle: 'Not Urgent & Not Important',
      color: 'gray',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      icon: CheckCircle,
      tasks: tasks?.filter(t => categorizeTask(t) === 'not-urgent-not-important')
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Eisenhower Matrix</h3>
        <p className="text-sm text-gray-600">Tasks organized by urgency and importance</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(quadrants)?.map(([key, quadrant]) => {
          const Icon = quadrant?.icon;
          return (
            <div
              key={key}
              className={`${quadrant?.bgColor} ${quadrant?.borderColor} border-2 rounded-lg p-4`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon className={`w-5 h-5 text-${quadrant?.color}-600`} />
                <div>
                  <h4 className="font-semibold text-gray-900">{quadrant?.title}</h4>
                  <p className="text-xs text-gray-600">{quadrant?.subtitle}</p>
                </div>
              </div>

              <div className="space-y-2">
                {quadrant?.tasks?.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No tasks</p>
                ) : (
                  quadrant?.tasks?.slice(0, 3)?.map((task) => (
                    <div
                      key={task?.id}
                      onClick={() => onTaskClick?.(task)}
                      className="p-3 bg-white rounded border border-gray-200 hover:border-purple-300 cursor-pointer transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">{task?.title}</p>
                      {task?.estimated_duration && (
                        <p className="text-xs text-gray-500 mt-1">
                          {task?.estimated_duration} min
                        </p>
                      )}
                    </div>
                  ))
                )}
                {quadrant?.tasks?.length > 3 && (
                  <p className="text-xs text-gray-500 text-center pt-2">
                    +{quadrant?.tasks?.length - 3} more
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PriorityMatrix;